import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { useUnsavedChanges } from '@/context/UnsavedChangesContext';
import {
  LayoutDashboard, FolderKanban, FileText, Wrench, Users,
  CalendarDays, Boxes,
  BarChart3, Settings, HelpCircle, ChevronLeft, LogOut, X,
  Receipt, ChevronDown, Phone
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const NAV_ITEMS = [
  // DASHBOARD - arriba de todo, solo, primera pantalla que ve el usuario
  { path: '/dashboard', icon: LayoutDashboard, labelKey: 'sidebar.dashboard', active: true, roles: ['admin', 'office', 'driller', 'pump_installer', 'superadmin'] },
  { type: 'divider' },

  // PRINCIPAL - Calendario primero
  { path: '/calendar', icon: CalendarDays, labelKey: 'sidebar.calendar', active: true, roles: ['admin', 'office', 'driller', 'pump_installer', 'superadmin'] },

  // GESTIÓN PRINCIPAL
  { type: 'label', textKey: 'sidebar.management', roles: ['admin', 'office', 'superadmin'] },
  { path: '/contacts', icon: Users, labelKey: 'sidebar.contacts', active: true, roles: ['admin', 'office', 'superadmin'] },
  { path: '/projects', icon: FolderKanban, labelKey: 'sidebar.projects', active: true, roles: ['admin', 'office', 'superadmin'] },
  { 
    id: 'estimates-group',
    type: 'expandable', 
    icon: FileText, 
    labelKey: 'sidebar.estimates', 
    roles: ['admin', 'office', 'superadmin'],
    children: [
      { path: '/bookings', labelKey: 'sidebar.leads', active: true },
      { path: '/estimates', labelKey: 'sidebar.allEstimates', active: true },
      { path: '/estimate-templates', labelKey: 'sidebar.templates', active: true },
    ]
  },
  { 
    id: 'invoices-group',
    type: 'expandable', 
    icon: Receipt, 
    labelKey: 'sidebar.invoices', 
    roles: ['admin', 'office', 'superadmin'],
    // Expenses lives inside this group ("todo lo que tenga que ver con el
    // dinero") — fall back to the expenses permission gate so a role that
    // isn't office/admin (e.g. driller with Expenses view=true) can still
    // see this group to reach Expenses, without seeing the other children.
    permissionGate: { module: 'expenses', action: 'view' },
    children: [
      { path: '/invoices', labelKey: 'sidebar.allInvoices', active: true, roles: ['admin', 'office', 'superadmin'] },
      { path: '/invoice-templates', labelKey: 'sidebar.invoiceTemplates', active: true, roles: ['admin', 'office', 'superadmin'] },
      { path: '/electronic-payments', labelKey: 'sidebar.electronicPayments', active: true, roles: ['admin', 'office', 'superadmin'] },
      { path: '/expenses', labelKey: 'sidebar.expenses', active: true, roles: ['admin', 'office', 'superadmin'], permissionGate: { module: 'expenses', action: 'view' } },
    ]
  },
  
  // TRABAJOS - Expandible con sub-items
  { 
    id: 'jobs-group',
    type: 'expandable', 
    icon: Wrench, 
    labelKey: 'sidebar.jobs', 
    roles: ['admin', 'office', 'driller', 'pump_installer', 'superadmin'],
    children: [
      { path: '/jobs', labelKey: 'sidebar.allJobs', active: true, roles: ['admin', 'office', 'superadmin'] },
      { path: '/jobs?type=well_construction', labelKey: 'sidebar.jobWellConstruction', active: true, roles: ['admin', 'office', 'driller', 'superadmin'] },
      { path: '/jobs?type=pump_installer', labelKey: 'sidebar.jobPumpInstaller', active: true, roles: ['admin', 'office', 'pump_installer', 'superadmin'] },
      { path: '/jobs?type=filters_service', labelKey: 'sidebar.jobFiltersService', active: true, roles: ['admin', 'office', 'superadmin'] },
      { path: '/jobs?type=site_inspection', labelKey: 'sidebar.jobSiteInspection', active: true, roles: ['admin', 'office', 'superadmin'] },
      { path: '/permits', labelKey: 'sidebar.jobPermit', active: true, roles: ['admin', 'office', 'superadmin'] },
      { path: '/custom-forms', labelKey: 'sidebar.customForms', active: true, roles: ['admin', 'superadmin'] },
    ]
  },

  // OPERACIONES - agrupado en un solo desplegable para reducir el largo del menu
  // (Alian: "me gusta ver las cosas limpias... lo que no se usa a diario, agrupado")
  { 
    id: 'operations-group',
    type: 'expandable', 
    icon: Boxes, 
    labelKey: 'sidebar.operations', 
    roles: ['admin', 'office', 'driller', 'pump_installer', 'superadmin'],
    children: [
      {
        id: 'inventory-equipment-subgroup',
        type: 'subgroup',
        labelKey: 'sidebar.inventoryEquipment',
        children: [
          { path: '/inventory', labelKey: 'sidebar.inventory', active: true, roles: ['admin', 'office', 'superadmin'] },
          { path: '/equipment', labelKey: 'sidebar.equipment', active: true, roles: ['admin', 'office', 'driller', 'pump_installer', 'superadmin'] },
        ]
      },
      { path: '/maps', labelKey: 'sidebar.maps', active: true, roles: ['admin', 'office', 'driller', 'pump_installer', 'superadmin'] },
      { path: '/timecards', labelKey: 'sidebar.timecards', active: true, roles: ['admin', 'office', 'driller', 'pump_installer', 'superadmin'] },
    ]
  },

  // ADMIN
  { type: 'label', textKey: 'sidebar.analysis', roles: ['admin', 'superadmin'] },
  { path: '/phone', icon: Phone, labelKey: 'sidebar.calls', active: true, roles: ['admin', 'office', 'superadmin'] },
  { path: '/reports', icon: BarChart3, labelKey: 'sidebar.reports', active: true, roles: ['admin', 'superadmin'] },
  { path: '/settings', icon: Settings, labelKey: 'sidebar.settings', active: true, roles: ['admin', 'superadmin'] },
];

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();
  const { confirmLeave } = useUnsavedChanges();
  // Auto-expand group when a child (or nested subgroup child) route is active
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = {};
    const matchesPath = (path) => {
      if (path.includes('?')) {
        const [pathname, query] = path.split('?');
        return window.location.pathname === pathname && window.location.search === `?${query}`;
      }
      return window.location.pathname === path;
    };
    NAV_ITEMS.filter(i => i.type === 'expandable').forEach(group => {
      group.children?.forEach(child => {
        if (child.type === 'subgroup') {
          if (child.children?.some(sub => matchesPath(sub.path))) {
            initial[group.id] = true;
            initial[child.id] = true;
          }
        } else if (matchesPath(child.path)) {
          initial[group.id] = true;
        }
      });
    });
    return initial;
  });

  const handleNav = (path) => {
    if (!confirmLeave()) return;
    if (path.includes('?')) {
      const [pathname, query] = path.split('?');
      navigate(`${pathname}?${query}`);
    } else {
      navigate(path);
    }
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Check if any child (including nested subgroup children) of a group is active
  const isGroupActive = (children) => {
    return children?.some(child => {
      if (child.type === 'subgroup') return isGroupActive(child.children);
      if (child.path.includes('?')) {
        const [pathname, query] = child.path.split('?');
        return location.pathname === pathname && location.search === `?${query}`;
      }
      return location.pathname === child.path;
    });
  };

  const renderNavItem = (item, i) => {
    const userRole = user?.role || 'driller';
    if (item.roles && !item.roles.includes(userRole)) {
      const gate = item.permissionGate;
      if (!(gate && hasPermission(gate.module, gate.action))) return null;
    }

    // Same role+permissionGate rule as top-level items, applied to a group's
    // children — lets a role outside the group's main `roles` list (e.g. a
    // driller with only Expenses access) still see just that one child.
    // A subgroup (e.g. "Inventory & Equipment") is visible if at least one
    // of its nested children is visible for this role.
    const isChildVisible = (child) => {
      if (child.type === 'subgroup') {
        return child.children?.some(isChildVisible);
      }
      if (child.roles && !child.roles.includes(userRole)) {
        const gate = child.permissionGate;
        return !!(gate && hasPermission(gate.module, gate.action));
      }
      return true;
    };

    // Flatten a children list, resolving subgroups into their own children —
    // used only for the collapsed-sidebar fallback (pick first navigable path).
    const flattenPaths = (children) => children.reduce((acc, c) => (
      c.type === 'subgroup' ? [...acc, ...(c.children || [])] : [...acc, c]
    ), []);

    if (item.type === 'divider') {
      return <div key={i} className="my-3 border-t border-white/[0.06]" />;
    }

    if (item.type === 'label') {
      if (collapsed) return null;
      return (
        <p key={i} className="px-3 pt-4 pb-1.5 text-[9px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
          {t(item.textKey)}
        </p>
      );
    }

    // Expandable group (Jobs with sub-items)
    if (item.type === 'expandable') {
      const isExpanded = expandedGroups[item.id];
      const groupActive = isGroupActive(item.children);
      const Icon = item.icon;
      const label = t(item.labelKey);

      if (collapsed) {
        // When collapsed, show first VISIBLE child path on click
        const visibleChildren = flattenPaths(item.children).filter(isChildVisible);
        const firstPath = (visibleChildren[0] || flattenPaths(item.children)[0]).path;
        return (
          <TooltipProvider key={item.id} delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid={`nav-${item.id}`}
                  onClick={() => handleNav(firstPath)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative
                    ${groupActive
                      ? 'bg-white/[0.12] text-white shadow-sm'
                      : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'
                    }
                  `}
                >
                  {groupActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-[#3399FF] rounded-r-full" />
                  )}
                  <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium">
                {label}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }

      return (
        <div key={item.id}>
          <button
            data-testid={`nav-${item.id}`}
            onClick={() => toggleGroup(item.id)}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative
              ${groupActive
                ? 'bg-white/[0.08] text-white'
                : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'
              }
            `}
          >
            {groupActive && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-[#3399FF] rounded-r-full" />
            )}
            <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
            <span className="flex-1 text-left">{label}</span>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Sub-items */}
          <div className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="ml-4 pl-3 border-l border-white/[0.06] mt-1 space-y-0.5">
              {item.children.map((child, ci) => {
                // Check if child has role restrictions (with permissionGate fallback)
                if (!isChildVisible(child)) return null;

                // Nested subgroup (e.g. "Inventory & Equipment") — its own
                // mini toggle + indented links, one level deeper.
                if (child.type === 'subgroup') {
                  const subExpanded = expandedGroups[child.id];
                  const subActive = isGroupActive(child.children);
                  return (
                    <div key={child.id}>
                      <button
                        data-testid={`nav-${child.id}`}
                        onClick={() => toggleGroup(child.id)}
                        className={`
                          w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                          ${subActive ? 'text-white' : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'}
                        `}
                      >
                        <span className="flex-1 text-left">{t(child.labelKey)}</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${subExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      <div className={`overflow-hidden transition-all duration-200 ${subExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="ml-3 pl-3 border-l border-white/[0.06] space-y-0.5">
                          {child.children.filter(isChildVisible).map(sub => {
                            const isSubActive = location.pathname === sub.path;
                            return (
                              <button
                                key={sub.path}
                                data-testid={`nav-${sub.path.slice(1)}`}
                                onClick={() => handleNav(sub.path)}
                                className={`
                                  w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                                  ${isSubActive ? 'bg-white/[0.12] text-white' : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'}
                                `}
                              >
                                {t(sub.labelKey)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }

                let isChildActive;
                if (child.path.includes('?')) {
                  const [pathname, query] = child.path.split('?');
                  isChildActive = location.pathname === pathname && location.search === `?${query}`;
                } else {
                  isChildActive = location.pathname === child.path && !location.search;
                }
                return (
                  <button
                    key={child.path}
                    data-testid={`nav-${child.path.split('?')[0].slice(1)}${child.path.includes('?') ? '-' + child.path.split('type=')[1] : ''}`}
                    onClick={() => handleNav(child.path)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                      ${isChildActive
                        ? 'bg-white/[0.12] text-white'
                        : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'
                      }
                    `}
                  >
                    {t(child.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    // Regular nav item
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    const label = t(item.labelKey);
    
    const navBtn = (
      <button
        key={item.path}
        data-testid={`nav-${item.path.slice(1)}`}
        onClick={() => handleNav(item.path)}
        disabled={!item.active}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative
          ${item.indent && !collapsed ? 'ml-4' : ''}
          ${isActive
            ? 'bg-white/[0.12] text-white shadow-sm'
            : item.active
              ? 'text-slate-400 hover:text-white hover:bg-white/[0.06]'
              : 'text-slate-600 cursor-not-allowed opacity-50'
          }
        `}
      >
        {isActive && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-[#3399FF] rounded-r-full" />
        )}
        <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
        {!collapsed && <span>{label}</span>}
        {!item.active && !collapsed && (
          <span className="ml-auto text-[9px] uppercase tracking-wider text-slate-600 bg-slate-800 px-1.5 py-0.5 rounded">
            {t('sidebar.comingSoon')}
          </span>
        )}
      </button>
    );

    if (collapsed) {
      return (
        <TooltipProvider key={item.path} delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{navBtn}</TooltipTrigger>
            <TooltipContent side="right" className="font-medium">
              {label}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return navBtn;
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-white/[0.06]">
        <img
          src="https://customer-assets.emergentagent.com/job_blue-well-crm/artifacts/vyxcu57o_image.png"
          alt="Blue Well"
          className="w-9 h-9 rounded-lg object-contain bg-white/10 p-0.5"
        />
        {!collapsed && (
          <div className="animate-fade-in">
            <h2 className="text-sm font-bold text-white tracking-tight font-['Barlow']">Blue Well</h2>
            <p className="text-[10px] text-slate-400 font-medium tracking-wider uppercase">{t('auth.crmSubtitle')}</p>
          </div>
        )}
        <button
          data-testid="sidebar-collapse-btn"
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto text-slate-400 hover:text-white transition-colors hidden lg:flex"
        >
          <ChevronLeft className={`w-4 h-4 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
        </button>
        <button
          data-testid="sidebar-close-btn"
          onClick={() => setMobileOpen(false)}
          className="ml-auto text-slate-400 hover:text-white transition-colors lg:hidden"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-3">
        <nav className="px-3 space-y-0.5">
          {NAV_ITEMS.map((item, i) => renderNavItem(item, i))}
        </nav>
      </ScrollArea>

      {/* User section */}
      <div className="border-t border-white/[0.06] p-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#003366] to-[#3399FF] flex items-center justify-center text-white text-xs font-bold">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
          )}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="text-slate-500 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-white/[0.06]"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      {/* Mobile sidebar */}
      <aside
        data-testid="mobile-sidebar"
        className={`
          fixed top-0 left-0 h-full w-64 bg-[#0F172A] z-50 transform transition-transform duration-300 lg:hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sidebarContent}
      </aside>
      {/* Desktop sidebar */}
      <aside
        data-testid="desktop-sidebar"
        className={`
          hidden lg:flex flex-col h-screen bg-[#0F172A] border-r border-white/[0.06] fixed top-0 left-0 z-30
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-[68px]' : 'w-64'}
        `}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
